<template>
    <div
        class="font-brand-heading font-heading flex w-full text-6xl leading-tight"
        :class="{
            'text-subheading': color === 'accent',
            'text-heading': color !== 'accent',
            'justify-center': alignment === 'centred',
        }"
    >
        <div>{{ prefix }}</div>
        <div
            ref="counterRef"
            v-element-visibility="onElementVisibility"
            class="text-center"
        >
            {{ formatValue(counterValue) }}
        </div>
        <div>{{ suffix }}</div>
    </div>
</template>

<script setup>
    import { vElementVisibility } from '@vueuse/components'

    const props = defineProps({
        prefix: String,
        value: {
            type: [Number, String],
        },
        suffix: String,
        color: String,
        duration: {
            type: Number,
            default: 3000,
        },
        alignment: String,
    })

    const counterRef = ref(null)
    const counterValue = ref(0)
    const targetValue = computed(() => {
        if (typeof props.value === 'string') {
            const parsedValue = parseFloat(props.value)
            return isNaN(parsedValue) ? 0 : parsedValue
        }
        return props.value
    })
    const prefersReducedMotion = ref(false)
    const animationCleanup = ref(null)
    const hasAnimated = ref(false)

    const formatValue = (value) => {
        const numValue = Number(value)
        const targetHasDecimal = !Number.isInteger(targetValue.value)

        if (targetHasDecimal) {
            // If target has decimal, always show one decimal place
            return numValue.toFixed(1)
        } else {
            // If target is whole number, don't show any decimal places
            return Math.round(numValue).toString()
        }
    }

    const animateCounter = () => {
        if (prefersReducedMotion.value) {
            counterValue.value = targetValue.value
            return
        }

        const startValue = 0
        let startTimestamp = null
        let animationFrameId = null

        const hasDecimal = !Number.isInteger(targetValue.value)

        const isLargeNumber = targetValue.value > 100

        const updateCounter = (timestamp) => {
            if (!startTimestamp) startTimestamp = timestamp

            const progress = Math.min((timestamp - startTimestamp) / props.duration, 1)

            const animatedValue = startValue + (targetValue.value - startValue) * progress

            if (hasDecimal) {
                counterValue.value = Math.round(animatedValue * 10) / 10
            } else if (isLargeNumber) {
                counterValue.value = Math.floor(animatedValue / 10) * 10
            } else {
                counterValue.value = Math.floor(animatedValue)
            }

            if (progress < 1) {
                animationFrameId = requestAnimationFrame(updateCounter)
            } else {
                counterValue.value = targetValue.value
            }
        }

        animationFrameId = requestAnimationFrame(updateCounter)

        return () => {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId)
            }
        }
    }

    const onElementVisibility = (isVisible) => {
        if (isVisible && !hasAnimated.value) {
            hasAnimated.value = true
            setInitialWidth()
        }
    }

    const setInitialWidth = () => {
        if (counterRef.value && typeof window !== 'undefined') {
            // Measure the width to stop it bouncing around
            counterValue.value = targetValue.value

            nextTick(() => {
                const width = counterRef.value.offsetWidth
                counterRef.value.style.minWidth = `${width}px`

                counterValue.value = 0

                // Check for reduced motion
                prefersReducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches

                animationCleanup.value = animateCounter()
            })
        }
    }

    onMounted(() => {
        return () => {
            if (animationCleanup.value && typeof animationCleanup.value === 'function') {
                animationCleanup.value()
            }
        }
    })
</script>

<style scoped></style>
